import React, { useRef, useState, useEffect } from 'react';
import { Box, Flex } from '@dutchie/common/components/rebass-compat';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { StyledLink, SmallLoader } from 'shared/components';
import { isBlank } from 'underscore.string';
import PhotoIdCapture from 'shared/modals/photo-id-capture';
import { uploadSecureImage } from 'shared/graphql/misc/mutations';
import _ from 'lodash';
import compressImage from 'browser-image-compression';

import useViewport from 'hooks/use-viewport';
import { ModalWrapper } from 'shared/modals/v2-wrapper';
import useApolloClient from 'shared/hooks/use-apollo-client';
import useStores from 'shared/hooks/use-stores';
import useErnie from 'shared/hooks/use-ernie';
import CameraIcon from 'shared/assets/camera-icon';
import UploadIcon from 'shared/assets/upload-icon';
import IdSuccessIcon from 'shared/assets/id-success-icon';
import { mediaQueries } from 'shared/styles';
import { CheckoutText, CheckoutFieldHeader } from 'checkout/components/styles/common';

function PhotoIdUpload({
  value,
  guest,
  error,
  medical,
  onPhotoUploaded,
  onUploadError,
  onValueAfterError,
  onPhotoReset,
  id,
  disabled = false,
}) {
  const [loadingCamera, setLoadingCamera] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [hasVideoInput, setHasVideoInput] = useState(false);
  const [showPhotoIdCaptureModal, setShowPhotoIdCaptureModal] = useState(false);
  const { UI } = useStores();
  const viewport = useViewport();

  const uploadLabelRef = useRef();

  const containerProps = {
    isIframe: UI.isIframe,
    iframeOffset: viewport.offset,
    parentOffset: viewport.parentOffset,
    viewportHeight: viewport.height,
    viewportWidth: viewport.width,
    iframeHeight: UI.iframeHeight,
  };

  const apolloClient = useApolloClient();
  const showErnie = useErnie();

  const isEmbeddedOrDutchieMain = UI.isEmbedded || UI.isDutchieMain;

  useEffect(() => {
    let hasUnmounted = false;
    async function checkForVideo() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      if (devices) {
        devices.forEach((device) => {
          if (device.kind === 'videoinput' && !hasUnmounted) {
            setHasVideoInput(true);
          }
        });
      }
    }
    checkForVideo();
    return () => {
      hasUnmounted = true;
    };
  }, []);

  async function uploadFile(readEvent) {
    try {
      const res = await apolloClient.mutate({
        mutation: uploadSecureImage,
        variables: { input: { image: readEvent.target.result } },
      });
      if (typeof onPhotoUploaded === 'function') {
        onPhotoUploaded(res.data.uploadSecureImage.key);
      }
      setLoadingUpload(false);
      setLoadingCamera(false);
    } catch (e) {
      setLoadingUpload(false);
      setLoadingCamera(false);
      console.log(e);
      showErnie('There was an error uploading your image. Please contact support.', 'danger');
      if (typeof onUploadError === 'function') {
        onUploadError(e);
      }
    }
  }

  function handleCameraUpload(image) {
    setLoadingCamera(true);
    setShowPhotoIdCaptureModal(false);
    uploadFile({ target: { result: image } });
  }

  async function handleUpload(event) {
    if (!event.target.files[0]) {
      return;
    }
    if (event.target.files[0].size > 20000000) {
      showErnie('Your file is too large. Please upload a file smaller than 20MB', 'danger');
      return;
    }
    if (error) {
      onValueAfterError();
    }
    const allowedFileTypes = ['png', 'jpg', 'jpeg'];
    const fileExtension = event.target.files[0].name.split('.').pop().toLowerCase();
    if (!_.includes(allowedFileTypes, fileExtension)) {
      showErnie(`Images only please. Supported formats: .${allowedFileTypes.join(', .')}`, 'danger');
      return;
    }

    setLoadingUpload(true);
    const reader = new FileReader();
    reader.onload = uploadFile;
    try {
      const compressedFile = await compressImage(event.target.files[0], {
        maxSizeMB: 0.35,
      });

      reader.readAsDataURL(compressedFile);
    } catch (err) {
      reader.readAsDataURL(event.target.files[0]);
      console.log(err);
    }
  }

  function clearPhotoId() {
    if (typeof onPhotoReset === 'function') {
      onPhotoReset();
    }
  }

  function capturePhoto() {
    if (error) {
      onValueAfterError();
    }
    setShowPhotoIdCaptureModal(true);
  }

  function fileUploaded() {
    if (!isBlank(value)) {
      return true;
    }

    return false;
  }

  if (fileUploaded()) {
    return (
      <Flex pt='4px' pb={guest ? '4px' : '0px'} mb={medical ? '25px' : 0}>
        <IdSuccessIcon width={54} />
        <Box ml={10}>
          <CheckoutFieldHeader mb='3px' mt='5px'>
            {`${medical ? 'Medical card' : 'ID'} successfully uploaded!`}
          </CheckoutFieldHeader>
          <StyledLink as='button' fontSize='12px' onClick={clearPhotoId}>
            Upload new photo
          </StyledLink>
        </Box>
      </Flex>
    );
  }
  return (
    <Box mb={medical ? '25px' : 0} mt={guest ? '2px' : 0} pb={guest ? '7px' : '0px'} data-testid='photo-id-section'>
      <StyledCheckoutText width={guest ? 1 : [1, 1, 4 / 5]} error={error}>
        {medical && 'Upload a photo of your Medical Card'}
        {!medical && 'Upload a photo of your drivers license or government issued ID'}
      </StyledCheckoutText>
      <ButtonContainer guest={guest}>
        {hasVideoInput && (
          <Button
            onClick={capturePhoto}
            data-cy='use-camera-button'
            data-test='use-camera-button'
            data-testid='use-camera-button'
            disabled={disabled}
          >
            {loadingCamera ? (
              <SmallLoader height={20} color='#697a85' />
            ) : (
              <>
                <CameraIcon width={45} />
                <ButtonText>Use Camera</ButtonText>
              </>
            )}
          </Button>
        )}
        <label
          ref={uploadLabelRef}
          htmlFor={`${id}-upload`}
          data-cy='upload-file-button'
          data-test='upload-file-button'
          data-testid='upload-file-button'
        >
          <Button onClick={() => uploadLabelRef.current.click()} disabled={disabled}>
            {loadingUpload ? (
              <SmallLoader height={20} color='#697a85' />
            ) : (
              <>
                <UploadIcon width={45} />
                <ButtonText>Upload a file</ButtonText>
              </>
            )}
            <input
              type='file'
              accept='image/png, image/jpeg, image/jpg'
              id={`${id}-upload`}
              onChange={handleUpload}
              disabled={disabled}
              style={{ display: 'none' }}
            />
          </Button>
        </label>
      </ButtonContainer>
      {!isEmbeddedOrDutchieMain && showPhotoIdCaptureModal && (
        <PhotoIdCapture
          onRequestClose={() => setShowPhotoIdCaptureModal(false)}
          handleCameraUpload={handleCameraUpload}
        />
      )}
      {isEmbeddedOrDutchieMain && (
        <StyledModalWrapper open={showPhotoIdCaptureModal}>
          <PhotoIdCapture
            containerProps={containerProps}
            onRequestClose={() => setShowPhotoIdCaptureModal(false)}
            handleCameraUpload={handleCameraUpload}
          />
        </StyledModalWrapper>
      )}
    </Box>
  );
}

export default observer(PhotoIdUpload);

const StyledCheckoutText = styled(({ error: _error, ...props }) => <CheckoutText {...props} />)`
  color: ${(props) => (props.error ? '#e25241' : 'var(--dcm-checkout-text-muted-color, #9aa1a9)')};
  font-size: 12px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  max-width: 294px;
  width: 100%;
  margin-top: 12px;
  @media ${mediaQueries.phone} {
    margin-top: 0px;
  }
`;

const Button = styled.button`
  width: 141px;
  height: 41px;
  border-radius: 5px;
  background-color: var(--dcm-checkout-save-info-bg, #f3f6f8);
  border: 1px solid var(--dcm-checkout-input-border-color, #c1ced6);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  outline: none;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:hover {
    background-color: var(--dcm-color-surface-hover, #e2e9ed);
  }
  > svg {
    margin-left: -10px;
  }
  @media ${mediaQueries.phone} {
    margin-top: 12px;
  }
`;

const ButtonText = styled.span`
  font-size: 13px;
  font-weight: bold;
  color: var(--dcm-checkout-text-muted-color, #697a85);
  line-height: 16px;
`;

const StyledModalWrapper = styled(ModalWrapper)`
  max-width: 650px;
  width: 100%;
  margin: 0 auto;
`;
